import { CityAttractionRO } from '@routellers/types/dist/city-attraction/city-attraction.ro';
import { CityQuestRO } from '@routellers/types/dist/city-quest/city-quest.ro';
import { CreateCityQuestDto } from '@routellers/types/dist/city-quest/create-city-quest.dto';
import {
    GenerateCityQuestAudioRO,
    GenerateCityQuestLocaleAudioDto,
    GenerateCityQuestStopAudioDto,
} from '@routellers/types/dist/city-quest/generate-city-quest-audio.dto';
import { ImportCityQuestDto } from '@routellers/types/dist/city-quest/import-city-quest.dto';
import { UpdateCityQuestDto } from '@routellers/types/dist/city-quest/update-city-quest.dto';
import { UpsertCityQuestLocaleDto } from '@routellers/types/dist/city-quest/upsert-city-quest-locale.dto';
import { UpsertCityQuestRouteEventsDto } from '@routellers/types/dist/city-quest/upsert-city-quest-route-event.dto';
import { UpsertCityQuestStopsDto } from '@routellers/types/dist/city-quest/upsert-city-quest-stop.dto';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';

import { ApiMethodDeclaration } from '..';

/** Admin API for city quests: technical fields, stops, route events, locales. */

const query: ApiMethodDeclaration<undefined, CityQuestRO[]> = () => ({
    url: '/admin/city-quests',
    method: 'GET',
});

const getOne: ApiMethodDeclaration<number, CityQuestRO> = (id) => ({
    url: `/admin/city-quests/${id}`,
    method: 'GET',
});

const create: ApiMethodDeclaration<{ data: CreateCityQuestDto }, CityQuestRO> = ({ data }) => ({
    url: '/admin/city-quests',
    method: 'POST',
    data,
});

/** Квест целиком плюс достопримечательности точек — для конструктора. */
const getContent: ApiMethodDeclaration<
    number,
    {
        quest: CityQuestRO;
        attractionsById: Record<string, CityAttractionRO>;
    }
> = (id) => ({
    url: `/admin/city-quests/${id}/content`,
    method: 'GET',
});

const upsertStops: ApiMethodDeclaration<
    { id: number; data: UpsertCityQuestStopsDto },
    CityQuestRO
> = ({ id, data }) => ({
    url: `/admin/city-quests/${id}/stops`,
    method: 'PATCH',
    data,
});

const upsertRouteEvents: ApiMethodDeclaration<
    { id: number; data: UpsertCityQuestRouteEventsDto },
    CityQuestRO
> = ({ id, data }) => ({
    url: `/admin/city-quests/${id}/route-events`,
    method: 'PATCH',
    data,
});

const upsertLocale: ApiMethodDeclaration<
    { id: number; data: UpsertCityQuestLocaleDto },
    CityQuestRO
> = ({ id, data }) => ({
    url: `/admin/city-quests/${id}/locales`,
    method: 'PATCH',
    data,
});

/** Готовность к публикации без смены статуса. */
const validate: ApiMethodDeclaration<
    number,
    { errors: string[]; warnings: string[]; playableLanguages: Languages[] }
> = (id) => ({
    url: `/admin/city-quests/${id}/validate`,
    method: 'GET',
});

/** TTS-озвучка текстов стопа (story / walk / challenge) после правок. */
const generateStopAudio: ApiMethodDeclaration<
    { stopId: number; data: GenerateCityQuestStopAudioDto },
    GenerateCityQuestAudioRO
> = ({ stopId, data }) => ({
    url: `/admin/city-quests/stops/${stopId}/generate-audio`,
    method: 'POST',
    data,
});

/** TTS-озвучка пролога/финала квест-локали после правок. */
const generateLocaleAudio: ApiMethodDeclaration<
    { id: number; data: GenerateCityQuestLocaleAudioDto },
    GenerateCityQuestAudioRO
> = ({ id, data }) => ({
    url: `/admin/city-quests/${id}/generate-audio`,
    method: 'POST',
    data,
});

const importGame: ApiMethodDeclaration<
    { data: ImportCityQuestDto },
    { quest: CityQuestRO; warnings: string[] }
> = ({ data }) => ({
    url: '/admin/city-quests/import',
    method: 'POST',
    data,
});

const update: ApiMethodDeclaration<{ id: number; data: UpdateCityQuestDto }, CityQuestRO> = ({
    id,
    data,
}) => ({
    url: `/admin/city-quests/${id}`,
    method: 'PATCH',
    data,
});

const markReady: ApiMethodDeclaration<number, CityQuestRO> = (id) => ({
    url: `/admin/city-quests/${id}/mark-ready`,
    method: 'POST',
});

const remove: ApiMethodDeclaration<number, { success: boolean }> = (id) => ({
    url: `/admin/city-quests/${id}`,
    method: 'DELETE',
});

export type CityQuestAnalyticsRO = {
    sessionsTotal: number;
    sessionsFinished: number;
    completionRate: number;
    medianDurationSec: number;
    avgScore: number;
    issues: Array<{ id: number; stopId: number; text: string; createdAt: string }>;
    overrides: Array<{
        id: number;
        stopId: number;
        fallbackForced: boolean;
        reason: string | null;
    }>;
};

const analytics: ApiMethodDeclaration<number, CityQuestAnalyticsRO> = (id) => ({
    url: `/admin/city-quests/${id}/analytics`,
    method: 'GET',
});

export const cityQuestsApiDeclaration = {
    query,
    getOne,
    create,
    getContent,
    upsertStops,
    upsertRouteEvents,
    upsertLocale,
    validate,
    generateStopAudio,
    generateLocaleAudio,
    importGame,
    update,
    markReady,
    remove,
    analytics,
};
